// @intent: Pure helpers for the §10 playthrough verifier's runtime side (U4b-3) — turn a runtime.driveInput command into
//          the REAL pointer-event sequence to dispatch on the .runtime-input-layer (no shortcut), and assemble a
//          runtime.match3Snapshot from the live window.__match3 logical state + the rendered client-px bounds.
// @flow: parent sends runtime.driveInput {design from→to} → driveInputPointerSequence → PixiRuntimeCanvas dispatches real
//        PointerEvents on the input layer → the playable's api.input.onDrag fires through the normal path. parent sends
//        runtime.queryMatch3 → buildMatch3Snapshot(window.__match3, board/cta Pixi bounds) → runtime.match3Snapshot reply.
// @stale: keep designToClient the exact inverse of PixiRuntimeCanvas.dispatchInput; keep the snapshot shape aligned with
//        the runtime.match3Snapshot protocol payload (packages/shared-types/src/playable-runtime-protocol.ts).

import type { RuntimeDriveInputPayload, RuntimeRect } from '@mintyads/shared-types';

export interface RuntimeViewportTransform {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

export interface DispatchablePointer {
  type: 'pointerdown' | 'pointermove' | 'pointerup';
  clientX: number;
  clientY: number;
}

/** Design-space → client px relative to the input-layer rect — the exact inverse of PixiRuntimeCanvas.dispatchInput. */
export function designToClient(
  designX: number,
  designY: number,
  t: RuntimeViewportTransform,
  layerLeft: number,
  layerTop: number,
): { clientX: number; clientY: number } {
  return {
    clientX: designX * t.scaleX + t.offsetX + layerLeft,
    clientY: designY * t.scaleY + t.offsetY + layerTop,
  };
}

/**
 * Ordered REAL pointer events for a drive command — a drag is pointerdown@from → pointermove(mid) → pointermove@to →
 * pointerup@to; a tap is pointerdown@from → pointerup@from. The runtime dispatches these as actual PointerEvents on the
 * .runtime-input-layer so the playable's input fires through the SAME path real input takes (never a direct handler call).
 */
export function driveInputPointerSequence(
  payload: RuntimeDriveInputPayload,
  t: RuntimeViewportTransform,
  layerLeft = 0,
  layerTop = 0,
): DispatchablePointer[] {
  const from = designToClient(payload.fromDesignX, payload.fromDesignY, t, layerLeft, layerTop);
  const to = designToClient(payload.toDesignX, payload.toDesignY, t, layerLeft, layerTop);
  if (payload.gesture === 'tap') {
    return [
      { type: 'pointerdown', clientX: from.clientX, clientY: from.clientY },
      { type: 'pointerup', clientX: from.clientX, clientY: from.clientY },
    ];
  }
  const mid = { clientX: (from.clientX + to.clientX) / 2, clientY: (from.clientY + to.clientY) / 2 };
  return [
    { type: 'pointerdown', clientX: from.clientX, clientY: from.clientY },
    { type: 'pointermove', clientX: mid.clientX, clientY: mid.clientY },
    { type: 'pointermove', clientX: to.clientX, clientY: to.clientY },
    { type: 'pointerup', clientX: to.clientX, clientY: to.clientY },
  ];
}

/** The live logical hooks the generated code exposes on window.__match3 (read in-browser). */
export interface Match3Hooks {
  boardTileCount: () => number;
  objectiveRemaining: () => number;
  ctaShown: () => boolean;
  firstMoveDesign: () => Match3DesignMove | null;
  nextValidSwapDesign: () => Match3DesignMove | null;
}

export interface Match3DesignPoint {
  x: number;
  y: number;
}

export interface Match3DesignMove {
  from: Match3DesignPoint;
  to: Match3DesignPoint;
}

export interface Match3SnapshotPayload {
  boardBounds: RuntimeRect | null;
  ctaBounds: RuntimeRect | null;
  tutorialHandBounds: RuntimeRect | null;
  firstMoveDesign: Match3DesignMove | null;
  nextValidSwapDesign: Match3DesignMove | null;
  boardTileCount: number;
  objectiveRemaining: number;
  ctaShown: boolean;
}

/** The imperative handle PixiRuntimeCanvas exposes to RuntimeApp's message router for the §10 verifier. */
export interface RuntimeMatch3Controls {
  driveInput: (payload: RuntimeDriveInputPayload) => void;
  getMatch3Snapshot: () => Match3SnapshotPayload;
}

export function isMatch3BoardLayerId(id: string): boolean {
  return id.startsWith('match-root-') || id === 'match-board' || id === 'native-match3-board-panel';
}

export function isMatch3CtaLayerId(id: string): boolean {
  return id === 'cta' || id === 'cta-wrapper' || id === 'packshot-button' || id === 'native-match3-cta';
}

export function chooseMostSpecificMatch3Rect(rects: readonly RuntimeRect[]): RuntimeRect | null {
  let best: RuntimeRect | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const rect of rects) {
    if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y) || !Number.isFinite(rect.width) || !Number.isFinite(rect.height)) continue;
    if (rect.width <= 0 || rect.height <= 0) continue;
    const area = rect.width * rect.height;
    if (area < bestArea) {
      best = rect;
      bestArea = area;
    }
  }
  return best;
}

/** Fail-closed snapshot when the runtime controls / Pixi canvas are not ready — the §10 verifier blocks on boardTileCount 0. */
export const FAIL_CLOSED_MATCH3_SNAPSHOT: Match3SnapshotPayload = {
  boardBounds: null,
  ctaBounds: null,
  tutorialHandBounds: null,
  firstMoveDesign: null,
  nextValidSwapDesign: null,
  boardTileCount: 0,
  objectiveRemaining: 0,
  ctaShown: false,
};

function safeInt(fn: () => number): number {
  try {
    const v = fn();
    return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : 0;
  } catch {
    return 0;
  }
}
function safeBool(fn: () => unknown): boolean {
  try {
    return fn() === true;
  } catch {
    return false;
  }
}
function safeMove(fn: () => Match3DesignMove | null): Match3DesignMove | null {
  try {
    const move = fn();
    if (!move) return null;
    const points = [move.from, move.to];
    if (points.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) return move;
    return null;
  } catch {
    return null;
  }
}

/**
 * Combine the LOGICAL state (window.__match3) with the RENDERED client-px bounds (board/CTA Pixi node bounds). A missing
 * window.__match3 hook ⇒ fail-closed defaults (boardTileCount 0, objectiveRemaining 0, ctaShown false) so the §10
 * verifier's blocking checks (match-board-renders) reject it. The pairing of logical state + bounds is what lets the
 * verifier prove visual presence AND catch "logical changed but visuals didn't".
 */
export function buildMatch3Snapshot(
  hooks: Match3Hooks | undefined,
  boardBounds: RuntimeRect | null,
  ctaBounds: RuntimeRect | null,
  tutorialHandBounds: RuntimeRect | null,
): Match3SnapshotPayload {
  if (!hooks) {
    return {
      boardBounds,
      ctaBounds,
      tutorialHandBounds,
      firstMoveDesign: null,
      nextValidSwapDesign: null,
      boardTileCount: 0,
      objectiveRemaining: 0,
      ctaShown: false,
    };
  }
  return {
    boardBounds,
    ctaBounds,
    tutorialHandBounds,
    firstMoveDesign: safeMove(hooks.firstMoveDesign),
    nextValidSwapDesign: safeMove(hooks.nextValidSwapDesign),
    boardTileCount: safeInt(hooks.boardTileCount),
    objectiveRemaining: safeInt(hooks.objectiveRemaining),
    ctaShown: safeBool(hooks.ctaShown),
  };
}
